<template>
  <div class="flex flex-row p-1">
    <div
      class="flex h-62 w-54 flex-col rounded-xl border bg-zinc-200 shadow dark:bg-dark-3"
      :class="borderClass"
    >
      <div class="flex flex-row justify-between pl-3 pr-1 pt-2 text-2xl">
        <div class="relative flex flex-row">
          <em
            :class="[
              { 'mdi-sofa': roomGroup.usageType === 'LIVING' },
              { 'mdi-briefcase': roomGroup.usageType === 'BUSINESS' },
              { 'mdi-home-roof': roomGroup.usageType === 'GENERAL' },
              { 'mdi-stairs': isStaircase },
              'mdi ',
            ]"
          />
          <div
            class="ml-1 self-center break-all text-sm text-gray-500 dark:text-gray-400"
          >
            {{ roomGroup.businessType }}
            <br />
          </div>
          <em
            v-if="isRoomGroupComplete"
            data-testId="roomGroupCompleteIcon"
            class="mdi mdi-check-bold absolute left-3 top-3 text-primary-2"
          ></em>
        </div>
        <BasePrompt
          :open="isDeletePromptOpen"
          :close="toggleDeletePrompt"
          :cancel="toggleDeletePrompt"
          :proceed="deleteRoomgroup"
          title="Raumgruppe löschen"
          question="Sind Sie sicher, dass Sie diese Raumgruppe löschen möchten?"
        />
        <BasePrompt
          :open="isDeleteDevicesPromptOpen"
          :close="toggleDeleteDevicesPrompt"
          :cancel="toggleDeleteDevicesPrompt"
          :proceed="removeDevicesFromRoomGroup"
          title="Testgeräte entfernen"
          question="Sind Sie sicher, dass Sie alle Testgeräte entfernen möchten?"
        />
        <BasePrompt
          :open="isAccessiblePromptOpen"
          :close="toggleAccessiblePrompt"
          :cancel="cancelIsAccessibleChange"
          :proceed="changeToIsAccessible"
          title="Diese Raumgruppe ist als nicht zugänglich definiert."
          cancel-text="Weiterhin kein Zutritt"
          proceed-text="Raumgruppe zugänglich"
        >
          <BaseChip
            text="Kein Zutritt"
            type="warning"
            class="text-xs"
            icon="mdi-cancel"
            data-testId="notAccessibleChip"
          />
        </BasePrompt>
        <div class="flex">
          <OptionsDropdown
            v-if="!isStaircase"
            width="w-max min-w-60"
            icon="mdi-dots-vertical"
          >
            <li
              v-if="isOnSiteInspection"
              class="text-light-warning dark:text-dark-warning"
              data-testid="roomGroupOptionStaircaseTest"
              @click="openStaircaseTestForm"
            >
              <em class="mdi mdi-access-point mr-2" />{{ staircaseTestLabel }}
            </li>
            <li
              v-if="!roomGroup.isAccessible"
              class="text-light-warning dark:text-dark-warning"
              @click="editRoomGroup({ scrollToBottom: true })"
            >
              <em class="mdi mdi-lock mr-2" />Hinderungsgrund aktualisieren
            </li>
            <li data-testid="roomGroupOptionEdit" @click="editRoomGroup">
              <em class="mdi mdi-pencil mr-2" />Bearbeiten
            </li>
            <li
              v-if="isRoomGroupComplete"
              data-testid="roomGroupOptionFinish"
              @click="toggleRoomGroupProgress"
            >
              <em
                class="mdi mdi-check mr-2"
                :class="{ 'mdi-close': isRoomGroupComplete }"
              />{{ roomGroupProgressLabel }}
            </li>
            <li
              v-if="isOnSiteInspection"
              data-testid="roomGroupOptionRemoveDevices"
              @click="toggleDeleteDevicesPrompt"
            >
              <em class="mdi mdi-trash-can mr-2" /> Testgeräte entfernen
            </li>
            <li
              data-testid="roomGroupOptionDelete"
              :class="[
                {
                  'cursor-not-allowed text-light-disabled-text hover:bg-transparent dark:text-dark-disabled-text':
                    !isRoomgroupDeletable,
                },
              ]"
              @click="isRoomgroupDeletable && toggleDeletePrompt()"
            >
              <em class="mdi mdi-delete mr-2" />Löschen
            </li>
          </OptionsDropdown>
        </div>
      </div>
      <div
        class="mt-2 grow cursor-pointer flex flex-col"
        data-testId="roomGroupName"
        @click="goToRoomGroup"
      >
        <div class="px-3 text-gray-500 dark:text-gray-400">
          <span v-if="roomGroup.customerAssignmentReference"
            >{{ roomGroup.customerAssignmentReference }} / </span
          >{{ roomGroup.number }}
          <br />
        </div>
        <div
          class="line-clamp-2 break-words px-3 text-xl font-bold dark:text-slate-100"
        >
          {{ roomGroupLabel }}
        </div>
        <div
          v-if="appointmentTimeRange"
          class="mt-auto px-3 pb-2 text-base font-bold dark:text-slate-100"
        >
          {{ appointmentTimeRange }}
        </div>
      </div>
      <div
        v-if="!roomGroup.isAccessible || roomGroup.isVacant"
        class="flex gap-2 px-2 py-3"
        data-testid="roomGroup-status-chips"
      >
        <BaseChip
          v-if="!roomGroup.isAccessible"
          text="Kein Zutritt"
          :type="
            roomGroup.typeNotAccessible === ImpedimentType.PERMANENT
              ? 'danger'
              : 'warning'
          "
          class="text-xs"
          :icon="impedimentIconClass(roomGroup.typeNotAccessible)"
          :outlined="isDarkMode"
        />
        <BaseChip
          v-if="roomGroup.isVacant"
          text="Leerstand"
          type="info"
          class="text-xs"
          :outlined="isDarkMode"
        />
        <BaseChip
          v-if="roomGroupDataMaps.funkcheckErrorCount.get(roomGroup.id)"
          type="warning"
          icon="mdi-help"
          class="text-xs"
        />
      </div>
      <DeviceListDropdown
        :room-group="roomGroup"
        :device-tags="roomGroupDataMaps.deviceTags.get(roomGroup.id)"
        :active-installation-points="
          roomGroupDataMaps.activeInstallationPoints.get(roomGroup.id) || []
        "
        :unmounted-devices="
          roomGroupDataMaps.unmountedDevices.get(roomGroup.id) || []
        "
        :no-signal-devices="
          roomGroupDataMaps.noSignalDevices.get(roomGroup.id) || []
        "
        :is-installation-location="
          roomGroupDataMaps.isInstallationLocation.get(roomGroup.id) || false
        "
        :active-inspection-gateways="
          roomGroupDataMaps.activeInspectionGateways.get(roomGroup.id) || []
        "
        :is-on-site-inspection="isOnSiteInspection"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, type ComputedRef, inject, ref } from 'vue';
import { Roomgroup } from '@/models';
import { ImpedimentType, impedimentIconClass } from '@/enums/generic';
import DeviceListDropdown from '@/components/BuildingOverview/DeviceListDropdown.vue';
import OptionsDropdown from '@/components/UI/Dropdown/OptionsDropdown.vue';
import BasePrompt from '@/components/UI/Modal/BasePrompt.vue';
import BaseChip from '@/components/UI/Chip/BaseChip.vue';
import type {
  RoomGroupDataMaps,
  RoomGroupHandlers,
} from '@/composables/useBuildingOverview';
import { formatTime } from '@/utils/dateUtils';

const props = defineProps<{
  roomGroup: Roomgroup;
  isStaircase?: boolean;
  isLast: boolean;
  roomGroupDataMaps: RoomGroupDataMaps;
  isOnSiteInspection: boolean;
}>();

const roomGroupHandlers = inject<RoomGroupHandlers>('roomGroupHandlers');
const isDarkMode = inject<ComputedRef<boolean>>(
  'isDarkMode',
  computed(() => false)
);
const isDragging = inject('isDragging', ref(false));

const isDeletePromptOpen = ref(false);
const isAccessiblePromptOpen = ref(false);
const isDeleteDevicesPromptOpen = ref(false);

const toggleDeletePrompt = () => {
  isDeletePromptOpen.value = !isDeletePromptOpen.value;
};
const toggleAccessiblePrompt = () => {
  isAccessiblePromptOpen.value = !isAccessiblePromptOpen.value;
};
const toggleDeleteDevicesPrompt = () => {
  isDeleteDevicesPromptOpen.value = !isDeleteDevicesPromptOpen.value;
};

const isOnSiteInspection = computed(() => props.isOnSiteInspection);
const progressData = computed(() =>
  props.roomGroupDataMaps.roomGroupProgress.get(props.roomGroup.id)
);

const isRoomGroupComplete = computed(
  () => progressData.value?.isComplete ?? false
);

const roomGroupProgressLabel = computed(
  () => progressData.value?.label ?? 'als fertig markieren'
);

const hasStaircaseTestDevices = computed(
  () =>
    props.roomGroupDataMaps.hasStaircaseTestDevices.get(props.roomGroup.id) ??
    false
);

const emit = defineEmits(['update-entities', 'open-staircase-test-form']);

const staircaseTestLabel = computed(() => {
  return hasStaircaseTestDevices.value
    ? 'Messung vor der Tür bearbeiten'
    : 'Messung vor der Tür';
});

const isRoomgroupDeletable = computed(() => {
  if (props.isStaircase) return false;
  return (
    props.roomGroupDataMaps.isRoomgroupDeletable.get(props.roomGroup.id) ??
    false
  );
});

const borderClass = computed(() => {
  return props.roomGroupDataMaps.unfinishedRoomGroups.get(props.roomGroup.id)
    ? 'border-2 border-light-error dark:border-dark-error'
    : 'border-zinc-300 dark:border-dark-4';
});

const roomGroupLabel = computed(() => {
  if (props.isStaircase) {
    return 'Hausflur / Treppenhaus';
  }
  if (props.roomGroup.usageType === 'GENERAL') {
    return 'Allgemeine Nutzung';
  }
  return props.roomGroup.doorbellName;
});

const appointmentTimeRange = computed(() => {
  const appointment = props.roomGroupDataMaps.appointmentByRoomGroupId.get(
    props.roomGroup.id
  );
  if (!appointment?.start || !appointment?.end) return '';
  return `${formatTime(appointment.start)} - ${formatTime(appointment.end)}`;
});

const deleteRoomgroup = () => {
  isDeletePromptOpen.value = false;
  roomGroupHandlers?.delete(props.roomGroup);
};

const toggleRoomGroupProgress = () => {
  roomGroupHandlers?.toggleProgress(props.roomGroup);
};

const enterRoomGroup = () => {
  roomGroupHandlers?.enter(props.roomGroup);
};

const changeToIsAccessible = () => {
  roomGroupHandlers?.changeAccessible(props.roomGroup, true);
  toggleAccessiblePrompt();
  enterRoomGroup();
};

const cancelIsAccessibleChange = () => {
  toggleAccessiblePrompt();
  enterRoomGroup();
};

const removeDevicesFromRoomGroup = () => {
  isDeleteDevicesPromptOpen.value = false;
  roomGroupHandlers?.removeDevices(props.roomGroup);
};

const goToRoomGroup = () => {
  if (isDragging.value) {
    return;
  }

  return props.roomGroup.isAccessible
    ? enterRoomGroup()
    : toggleAccessiblePrompt();
};
const editRoomGroup = (options?: { scrollToBottom?: boolean }) => {
  roomGroupHandlers?.edit(props.roomGroup, options);
};

const openStaircaseTestForm = () => {
  emit('open-staircase-test-form', props.roomGroup.id);
};
</script>
